import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput } from '@mylighthouse/prism-ember';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import SettingsCurrencyConversionCurrencyPickerMenu from 'lumen/components/settings/currency-conversion/currency-picker/menu';
import type { Currency } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { timeout } from 'lumen/utils/waiters';

import styles from './multiple-selection-currency-picker.module.css';

interface SettingsCurrencyConversionMultipleSelectionCurrencyPickerSignature {
  Args: {
    baseCurrency: Currency;
    isOpen: boolean;
    onApply: (currencyCodes: string[]) => unknown;
    portfolioCurrencyCodes: string[];
    selectedProfileCurrencyCodes: string[];
    setIsOpen: (isOpen: boolean) => void;
  };
}

export default class SettingsCurrencyConversionMultipleSelectionCurrencyPicker extends Component<SettingsCurrencyConversionMultipleSelectionCurrencyPickerSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked searchValue = '';
  @tracked selectedCurrencyCodesSet = new Set<string>();

  fetchCurrenciesTask = task({ restartable: true }, async () => {
    const userId = this.hotelAndMarketService.user.id;

    try {
      return await this.store.query<Currency>('currency', {
        namespace: APIGATEWAY,
        typeKey: `users/${userId}/currencies`,
        reference_currency: this.baseCurrency.code,
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  onSearchInputTask = task(
    { restartable: true },
    async (value: string): Promise<void> => {
      this.searchValue = value;

      if (value) {
        await timeout();

        this.usageAnalytics.track('Exchange Rate Currency Searched', {
          eventTrigger: 'currency-searched',
        });
      }
    },
  );

  trackCurrencySelectedTask = task(
    { restartable: true },
    async (): Promise<void> => {
      await timeout();

      this.usageAnalytics.track('Exchange Rate Currency Selected', {
        eventTrigger: 'currency-selected',
      });
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseCurrency() {
    return this.args.baseCurrency;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencies() {
    return this.fetchCurrenciesTask.last?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredCurrencies() {
    return this.currencies
      .filter((currency) => currency.code !== this.baseCurrency.code)
      .filter(
        (currency) =>
          currency.code
            .toLowerCase()
            .includes(this.searchValue.toLowerCase()) ||
          currency.name
            .toLowerCase()
            .includes(this.searchValue.toLowerCase()) ||
          currency.symbol
            .toLowerCase()
            .includes(this.searchValue.toLowerCase()),
      );
  }

  get hasChanges(): boolean {
    const hasCurrencyCodeSizeChanged =
      this.selectedCurrencyCodesSet.size !==
      this.initialSelectedCurrencyCodesSet.size;
    const isNewCurrencyPresent = Array.from(this.selectedCurrencyCodesSet).some(
      (code) => !this.initialSelectedCurrencyCodesSet.has(code),
    );

    return hasCurrencyCodeSizeChanged || isNewCurrencyPresent;
  }

  get initialSelectedCurrencyCodesSet(): Set<string> {
    return new Set(this.args.selectedProfileCurrencyCodes);
  }

  get portfolioCurrencyCodesSet(): Set<string> {
    return new Set(this.args.portfolioCurrencyCodes);
  }

  get selectedCurrencies(): Currency[] {
    return this.currencies
      .filter((currency) => currency.code !== this.baseCurrency.code)
      .filter((currency) => this.selectedCurrencyCodesSet.has(currency.code));
  }

  get showSelectedCurrenciesSection(): boolean {
    return (
      this.selectedCurrencyCodesSet.size > 0 &&
      this.searchValue === '' &&
      this.selectedCurrencies.length > 0
    );
  }

  constructor(
    owner: unknown,
    args: SettingsCurrencyConversionMultipleSelectionCurrencyPickerSignature['Args'],
  ) {
    // @ts-expect-error: Owner type mismatch between unknown and Glimmer Owner
    super(owner, args);
    void this.fetchCurrenciesTask.perform();

    this.selectedCurrencyCodesSet = new Set(
      this.args.selectedProfileCurrencyCodes,
    );
  }

  @action
  getCurrencySubLabel(currency: Currency): string {
    const roundedReferenceValue = this.intl.formatNumber(
      currency.referenceValue,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      },
    );

    return `1 ${this.baseCurrency.code} = ${roundedReferenceValue} ${currency.code}`;
  }

  @action
  isCurrencyDisabled(currency: Currency): boolean {
    return this.portfolioCurrencyCodesSet.has(currency.code);
  }

  @action
  isCurrencySelected(currency: Currency): boolean {
    return this.selectedCurrencyCodesSet.has(currency.code);
  }

  @action
  onApply(dropdown?: { toggleDropdown: () => void }): void {
    const selectedArray = Array.from(this.selectedCurrencyCodesSet);
    this.args.onApply(selectedArray);

    this.usageAnalytics.track('Exchange Rate Currency Added', {
      eventTrigger: 'currency-added',
    });

    this.resetDropdown();
    dropdown?.toggleDropdown();
  }

  @action
  onClose(dropdown?: { toggleDropdown: () => void }): void {
    this.resetDropdown();
    dropdown?.toggleDropdown();
  }

  @action
  resetDropdown(): void {
    this.selectedCurrencyCodesSet = new Set(
      this.initialSelectedCurrencyCodesSet,
    );
    this.searchValue = '';
  }

  @action
  setIsOpen(isOpen: boolean): void {
    this.args.setIsOpen(isOpen);

    if (!isOpen) {
      this.resetDropdown();
    }
  }

  @action
  toggleCurrency(currency: Currency, _selected?: boolean, event?: Event): void {
    // Stop propagation so the click doesn't bubble to the document-level outside-click
    // handler in ota-dropdown. Without this, if toggling removes the clicked element from
    // the DOM (e.g. unselecting the last item in the selected section), the detached
    // e.target causes .closest('.dropdown-target') to return null, incorrectly closing
    // the dropdown.
    event?.stopPropagation();

    const newSet = new Set(this.selectedCurrencyCodesSet);

    if (newSet.has(currency.code)) {
      newSet.delete(currency.code);
    } else {
      newSet.add(currency.code);
      void this.trackCurrencySelectedTask.perform();
    }

    this.selectedCurrencyCodesSet = newSet;
  }

  <template>
    <div class="relative inline-block">
      <div data-test-id="multiple-selection-currency-picker-container">
        {{! @glint-expect-error: Incorrect type }}
        <SettingsCurrencyConversionCurrencyPickerMenu
          @isOpen={{@isOpen}}
          @onClose={{this.resetDropdown}}
          @setIsOpen={{@setIsOpen}}
          class={{styles.picker}}
          data-test-id="multiple-selection-currency-picker"
        >
          <:header>
            <div class="flex">
              <PrismInput
                @autofocus={{true}}
                @iconAfter={{hash name="search"}}
                @onInput={{pick "target.value" this.onSearchInputTask.perform}}
                @placeholder={{t "general.search_currency"}}
                @removePadding={{true}}
                @value={{this.searchValue}}
                data-test-id="currency-picker-search"
              />
            </div>
          </:header>

          <:default as |Menu|>
            {{#if this.showSelectedCurrenciesSection}}
              <Menu.group
                @label={{t "general.selected"}}
                data-test-id="currency-picker-group-selected"
              >
                {{#each this.selectedCurrencies as |currency|}}
                  <Menu.item
                    @disabled={{this.isCurrencyDisabled currency}}
                    @onChange={{fn this.toggleCurrency currency}}
                    @selected={{true}}
                    @subLabel={{this.getCurrencySubLabel currency}}
                  >
                    <:default>
                      <div class="flex">
                        <span class="truncate" title={{currency.name}}>
                          {{currency.name}}
                        </span>
                        &nbsp;
                        <span>({{currency.symbol}})</span>
                      </div>
                    </:default>
                  </Menu.item>
                {{/each}}
              </Menu.group>
            {{/if}}

            <Menu.group
              @label={{t "general.all"}}
              data-test-id="currency-picker-group-all"
            >
              {{#each this.filteredCurrencies as |currency|}}
                <Menu.item
                  @disabled={{this.isCurrencyDisabled currency}}
                  @onChange={{fn this.toggleCurrency currency}}
                  @selected={{this.isCurrencySelected currency}}
                  @subLabel={{this.getCurrencySubLabel currency}}
                >
                  <:default>
                    <div class="flex">
                      <span class="truncate" title={{currency.name}}>
                        {{currency.name}}
                      </span>
                      &nbsp;
                      <span>({{currency.symbol}})</span>
                    </div>
                  </:default>
                </Menu.item>
              {{else}}
                <div
                  class="text-300--regular flex min-h-1000 items-center px-300"
                  data-test-id="currency-picker-no-results"
                >
                  {{t "general.no_results_found"}}
                </div>
              {{/each}}
            </Menu.group>
          </:default>

          <:footer as |footer|>
            <div class="flex items-center justify-end gap-200 p-200">
              <PrismButton
                @buttonType="secondary"
                @label={{t "general.cancel"}}
                {{on "click" (fn this.onClose footer.dropdown)}}
                data-test-id="currency-picker-cancel"
              />
              <PrismButton
                @buttonType="primary"
                @isDisabled={{not this.hasChanges}}
                @label={{t "general.apply"}}
                {{on "click" (fn this.onApply footer.dropdown)}}
                data-test-id="currency-picker-apply"
              />
            </div>
          </:footer>
        </SettingsCurrencyConversionCurrencyPickerMenu>
      </div>
    </div>
  </template>
}
